import React, { useState, useEffect, useLayoutEffect } from 'react';
import { images } from '../../assets';
import './HealingJourneyCarousel.css';
import FadeInSection from '../FadeInSection/FadeInSection';

const MOBILE_ITEM_WIDTH_PX = 270;
const INITIAL_ACTIVE_INDEX = 3; // Discover (middle of 7)

const carouselItems = [
  {
    id: 1,
    image: images.phone3,
    title: "Explore",
    description: "See how you feel right now"
  },
  {
    id: 2,
    image: images.phone6,
    title: "Programs",
    description: "Designed for you life"
  },
  {
    id: 3,
    image: images.phone5,
    title: "Healing Playlists",
    description: "Personalised for how you feel"
  },
  {
    id: 4,
    image: images.phone4,
    title: "Discover",
    description: "Choose from Immersive programs, breathwork and so much more"
  },
  {
    id: 5,
    image: images.phone0,
    title: "Now playing",
    description: "Personalised playlists + biometric sync"
  },
  {
    id: 6,
    image: images.phone1,
    title: "Insights",
    description: "Track you HRV & healing trends"
  },
  {
    id: 7,
    image: images.phone2,
    title: "Streaks",
    description: "Celebrate your progress"
  }
];

const DISCOVER_INDEX = carouselItems.findIndex((item) => item.title === 'Discover');
const DISCOVER_ACTIVE_INDEX = DISCOVER_INDEX >= 0 ? DISCOVER_INDEX : INITIAL_ACTIVE_INDEX;

const HealingJourneyCarousel: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(DISCOVER_ACTIVE_INDEX);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const measure = React.useCallback(() => {
    const container = containerRef.current ?? (document.querySelector('.carousel-container') as HTMLElement | null);
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setContainerWidth(rect.width);
  }, []);

  useLayoutEffect(() => {
    setActiveIndex(DISCOVER_ACTIVE_INDEX);
    measure();
    let raf2: number;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (typeof raf2 === 'number') cancelAnimationFrame(raf2);
    };
  }, [measure]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      measure();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [measure]);

  useEffect(() => {
    const el = containerRef.current ?? (document.querySelector('.carousel-container') as HTMLElement | null);
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const handleTransitionEnd = () => {
    setIsTransitioning(false);
  };

  const handlePrevClick = () => {
    if (!isTransitioning && activeIndex > 0) {
      setIsTransitioning(true);
      setActiveIndex(prev => prev - 1);
    }
  };

  const handleNextClick = () => {
    if (!isTransitioning && activeIndex < carouselItems.length - 1) {
      setIsTransitioning(true);
      setActiveIndex(prev => prev + 1);
    }
  };

  const calculateTransform = () => {
    const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const w = containerWidth > 0 ? containerWidth : viewportW;
    const itemCount = carouselItems.length;
    const isMobile = viewportW <= 768;

    if (isMobile) {
      const itemWidth = MOBILE_ITEM_WIDTH_PX;
      const centerOffset = w / 2 - itemWidth / 2;
      return centerOffset - activeIndex * itemWidth;
    }

    const itemWidth = w / itemCount;
    const centerOffset = (w - itemWidth) / 2;
    return centerOffset - activeIndex * itemWidth;
  };

  return (
    <section className="healing-journey-section">
      <div className="healing-journey-content">
        <FadeInSection>
          <h2 className="healing-journey-title">
            Your healing journey, in real time
          </h2>
          <p className="healing-journey-subtitle">
            Heal is the daily ritual designed to regulate your nervous system
          </p>
        </FadeInSection>
        <div className="carousel-container" ref={containerRef}>
          <button 
            className={`carousel-arrow ${activeIndex === 0 ? 'disabled' : ''}`} 
            onClick={handlePrevClick}
            disabled={activeIndex === 0}
          >
            <img src={isMobile ? images.arrowLeftMobile : images.arrowLeft} alt="Previous" />
          </button>
          <div 
            className="carousel-grid"
            style={{
              transform: `translateX(${calculateTransform()}px)`,
              width: '100%',
              transition: isTransitioning ? 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {carouselItems.map((item, index) => (
              <div 
                key={item.id}
                className={`carousel-item ${index === activeIndex ? 'active' : ''}`}
              >
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="carousel-item-image"
                />
                <h3 className="carousel-item-title">{item.title}</h3>
                <p className="carousel-item-description">{item.description}</p>
              </div>
            ))}
          </div>
          <button 
            className={`carousel-arrow ${activeIndex === carouselItems.length - 1 ? 'disabled' : ''}`} 
            onClick={handleNextClick}
            disabled={activeIndex === carouselItems.length - 1}
          >
            <img src={isMobile ? images.arrowRightMobile : images.arrowRight} alt="Next" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HealingJourneyCarousel; 